import * as React from "react";
import { useParams } from "react-router-dom";
import {
  Button,
  Confirm,
  Dropdown,
  Form,
  Header,
  Input,
  Label,
  Segment,
} from "semantic-ui-react";
import ProgrammeApi from "../../api/ProgrammeApi";
import { SideOverlay } from "../../components/SideOverlay";
import {
  HostFamilyContactModel,
  Rights_Programme,
  Rights_WebApp,
} from "../../generated";
import { DataTable, DataTableColumn } from "cool-it-react/dist/DataTable";
import ProgrammeHeader from "./ProgrammeHeader";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatISODate } from "cool-it-react/dist/utils/toDate";
import useRights from "../../hooks/UseRights";

interface IHostFamilyContactsProps {}

const emptyForm = (): HostFamilyContactModel => ({
  id: null,
  programmeID: null,
  participationID: undefined,
  hostName: "",
  hostAddress: "",
  hostPhoneNumber: "",
  hostEmail: "",
  checkInDate: "",
  checkOutDate: "",
  participantName: null,
  participantMyCISVID: null,
});

export const HostFamilyContacts: React.FC<IHostFamilyContactsProps> = () => {
  const { id, upn } = useParams<{ upn?: string; id?: string }>();
  const queryClient = useQueryClient();
  const globalRights = useRights();
  const isGlobalAdmin = globalRights.hasRight(Rights_WebApp.GlobalAdmin);

  const [panelOpen, setPanelOpen] = React.useState(false);
  const [formValues, setFormValues] =
    React.useState<HostFamilyContactModel>(emptyForm());
  const [deleteId, setDeleteId] = React.useState<string | null>(null);
  const [formErrors, setFormErrors] = React.useState<Record<string, string>>(
    {}
  );

  // ── Queries ────────────────────────────────────────────────────────────────

  const Programme = useQuery(["getProgrammeGET", id], () =>
    ProgrammeApi.getProgrammeGet(id)
  );

  const HostFamilyContactsQuery = useQuery(
    ["getHostFamilyContactsGET", id],
    () => ProgrammeApi.getHostFamilyContactsGet(id)
  );

  const ParticipantsQuery = useQuery(
    ["getHostFamilyContactParticipantsGET", id],
    () => ProgrammeApi.getHostFamilyContactParticipantsGet(id),
    { enabled: panelOpen }
  );

  // ── Rights ─────────────────────────────────────────────────────────────────

  const canEdit =
    isGlobalAdmin ||
    Programme.data?.rights?.some(
      (r) => r === Rights_Programme.HostFamilyContactEdit
    );

  // ── Mutations ──────────────────────────────────────────────────────────────

  const addMutation = useMutation(
    (model: HostFamilyContactModel) =>
      ProgrammeApi.addHostFamilyContactPost(id, model),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["getHostFamilyContactsGET", id]);
        closePanel();
      },
    }
  );

  const updateMutation = useMutation(
    (model: HostFamilyContactModel) =>
      ProgrammeApi.updateHostFamilyContactPut(id, model.id ?? undefined, model),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["getHostFamilyContactsGET", id]);
        closePanel();
      },
    }
  );

  const deleteMutation = useMutation(
    (contactId: string) =>
      ProgrammeApi.deleteHostFamilyContactDelete(id, contactId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["getHostFamilyContactsGET", id]);
        setDeleteId(null);
      },
    }
  );

  const isSaving = addMutation.isLoading || updateMutation.isLoading;

  // Clears only the specific field's error when user types in it
  const clearError = (field: string) => {
    if (formErrors[field]) {
      const next = { ...formErrors };
      delete next[field];
      setFormErrors(next);
    }
  };

  // ── Handlers ───────────────────────────────────────────────────────────────

  const closePanel = () => {
    setPanelOpen(false);
    setFormValues(emptyForm());
    setFormErrors({});
  };

  const handleAdd = () => {
    setFormValues(emptyForm());
    setFormErrors({});
    setPanelOpen(true);
  };

  const handleEdit = (record: HostFamilyContactModel) => {
    setFormValues({ ...record });
    setFormErrors({});
    setPanelOpen(true);
  };

  const handleParticipantSelect = (participationID: string) => {
    const participant = ParticipantsQuery.data?.find(
      (p) => p.participationID === participationID
    );
    setFormValues((prev) => ({
      ...prev,
      participationID,
      participantName: participant?.displayName ?? null,
      participantMyCISVID: participant?.myCISVID ?? null,
    }));
    clearError('participationID');
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formValues.hostName?.trim())
      errors.hostName = "Host Name is required.";
    if (!formValues.hostAddress?.trim())
      errors.hostAddress = "Host Address is required.";
    if (!formValues.hostPhoneNumber?.trim())
      errors.hostPhoneNumber = "Host Phone Number is required.";
    if (!formValues.hostEmail?.trim())
      errors.hostEmail = "Host Email is required.";
    if (!formValues.participationID)
      errors.participationID = "Participant is required.";
    if (!formValues.checkInDate)
      errors.checkInDate = "Check-in Date is required.";
    if (!formValues.checkOutDate)
      errors.checkOutDate = "Check-out Date is required.";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    if (formValues.id) {
      updateMutation.mutate(formValues);
    } else {
      addMutation.mutate(formValues);
    }
  };

  // ── Columns ────────────────────────────────────────────────────────────────

  const columns: DataTableColumn<HostFamilyContactModel>[] = [
    {
      id: "hostName",
      Header: "Host Name",
      accessor: (d) => d.hostName ?? "",
      width: 130,
    },
    {
      id: "hostAddress",
      Header: "Host Address",
      accessor: (d) => d.hostAddress ?? "",
      width: 160,
    },
    {
      id: "hostPhoneNumber",
      Header: "Host Phone Number",
      accessor: (d) => d.hostPhoneNumber ?? "",
      width: 130,
    },
    {
      id: "hostEmail",
      Header: "Host Email",
      accessor: (d) => d.hostEmail ?? "",
      width: 160,
    },
    {
      id: "participantName",
      Header: "Participant Name",
      accessor: (d) => d.participantName ?? "",
      width: 130,
    },
    {
      id: "participantMyCISVID",
      Header: "Participant myCISV ID",
      accessor: (d) => d.participantMyCISVID ?? "",
      width: 160,
    },
    {
      id: "checkInDate",
      Header: "Check-in Date",
      accessor: (d) => (d.checkInDate ? formatISODate(d.checkInDate) : ""),
      exportAccessor: (d) =>
        d.checkInDate ? formatISODate(d.checkInDate) : "",
      width: 110,
    },
    {
      id: "checkOutDate",
      Header: "Check-out Date",
      accessor: (d) => (d.checkOutDate ? formatISODate(d.checkOutDate) : ""),
      exportAccessor: (d) =>
        d.checkOutDate ? formatISODate(d.checkOutDate) : "",
      width: 110,
    },
    ...(canEdit
      ? ([
          {
            id: "actions",
            Header: "",
            accessor: (d: HostFamilyContactModel) => (
              <>
                <Button
                  icon="pencil"
                  size="mini"
                  secondary
                  compact
                  title="Edit"
                  onClick={() => handleEdit(d)}
                />
                <Button
                  icon="trash"
                  size="mini"
                  negative
                  compact
                  title="Delete"
                  onClick={() => d.id && setDeleteId(d.id)}
                />
              </>
            ),
            width: 80,
          },
        ] as DataTableColumn<HostFamilyContactModel>[])
      : []),
  ];

  // ── Dropdown options ───────────────────────────────────────────────────────

  const participantOptions =
    ParticipantsQuery.data?.map((p) => ({
      key: p.participationID,
      value: p.participationID,
      text: `${p.displayName ?? ""} (${p.myCISVID ?? ""})`,
    })) ?? [];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div>
      <ProgrammeHeader
        referenceNo={id}
        upn={upn}
        rights={Programme.data?.rights}
        loading={Programme.isLoading}
      />

      <Header as="h3">Host Family Contacts</Header>
      <p style={{ margin: "-0.5em 0 1.25em", fontSize: "0.875em", color: "#777" }}>
        Add one entry for each period a participant is staying with a host family. If a participant is staying twice with the same host family, you need to add each stay as a separate entry, but the contact person can be the same. 'Check-in date' is the date they arrive with the host family and 'check-out date' is the date they leave the host family.
      </p>

      <div style={{ position: "relative" }}>
        {canEdit && (
          <Button
            secondary
            size="small"
            icon="add"
            content="Add Host Family Contact"
            onClick={handleAdd}
            style={{ position: "absolute", top: "5px", right: "130px", zIndex: 1 }}
          />
        )}
        <DataTable
          header={null}
          exportFileName={"HostFamilyContacts_" + id}
          loading={HostFamilyContactsQuery.isLoading}
          scrollHorizontal={true}
          columns={columns}
          data={HostFamilyContactsQuery.data ?? []}
        />
      </div>

      {/* ── Side Panel ───────────────────────────────────────────────────── */}

      <SideOverlay
        visible={panelOpen}
        onHide={closePanel}
        dimmed
        className="edit-overlay"
      >
        <Segment basic style={{ minHeight: "50%" }}>
          <Header as="h2">
            {formValues.id
              ? "Edit Host Family Contact"
              : "Add Host Family Contact"}
          </Header>

          <Form loading={isSaving}>
            <Form.Field required error={!!formErrors.hostName}>
              <label>Full Name</label>
              <Input
                value={formValues.hostName ?? ""}
                placeholder="e.g. John Smith"
                onChange={(_, { value }) => {
                  setFormValues((prev) => ({ ...prev, hostName: value }));
                  clearError('hostName');
                }}
              />
              {formErrors.hostName && (
                <Label basic color="red" pointing>
                  {formErrors.hostName}
                </Label>
              )}
            </Form.Field>

            <Form.Field required error={!!formErrors.hostAddress}>
              <label>
                Address{" "}
                <span style={{ fontSize: "0.82em", color: "#999", fontWeight: "normal" }}>(incl. postal/ZIP code)</span>
              </label>
              <Input
                value={formValues.hostAddress ?? ""}
                placeholder="e.g. 12 Oak Street, Melbourne VIC 3000"
                onChange={(_, { value }) => {
                  setFormValues((prev) => ({ ...prev, hostAddress: value }));
                  clearError('hostAddress');
                }}
              />
              {formErrors.hostAddress && (
                <Label basic color="red" pointing>
                  {formErrors.hostAddress}
                </Label>
              )}
            </Form.Field>

            <Form.Field required error={!!formErrors.hostPhoneNumber}>
              <label>Host Phone Number</label>
              <Input
                value={formValues.hostPhoneNumber ?? ""}
                placeholder="e.g. +61 412 345 678"
                onChange={(_, { value }) => {
                  setFormValues((prev) => ({ ...prev, hostPhoneNumber: value }));
                  clearError('hostPhoneNumber');
                }}
              />
              {formErrors.hostPhoneNumber && (
                <Label basic color="red" pointing>
                  {formErrors.hostPhoneNumber}
                </Label>
              )}
            </Form.Field>

            <Form.Field required error={!!formErrors.hostEmail}>
              <label>Host Email</label>
              <Input
                type="email"
                value={formValues.hostEmail ?? ""}
                placeholder="e.g. smith@email.com"
                onChange={(_, { value }) => {
                  setFormValues((prev) => ({ ...prev, hostEmail: value }));
                  clearError('hostEmail');
                }}
              />
              {formErrors.hostEmail && (
                <Label basic color="red" pointing>
                  {formErrors.hostEmail}
                </Label>
              )}
            </Form.Field>

            <Form.Field required error={!!formErrors.participationID}>
              <label>Participant</label>
              <Dropdown
                selection
                search
                fluid
                loading={ParticipantsQuery.isLoading}
                options={participantOptions}
                value={formValues.participationID ?? ""}
                placeholder="Select participant..."
                onChange={(_, { value }) =>
                  handleParticipantSelect(value as string)
                }
              />
              {formErrors.participationID && (
                <Label basic color="red" pointing>
                  {formErrors.participationID}
                </Label>
              )}
            </Form.Field>

            <Form.Field>
              <label>Participant Name</label>
              <Input
                readOnly
                value={formValues.participantName ?? ""}
                placeholder="Auto-populated on participant selection"
              />
            </Form.Field>

            <Form.Field>
              <label>Participant myCISV ID</label>
              <Input
                readOnly
                value={formValues.participantMyCISVID ?? ""}
                placeholder="Auto-populated on participant selection"
              />
            </Form.Field>

            <Form.Field required error={!!formErrors.checkInDate}>
              <label>Check-in Date</label>
              <Input
                type="date"
                value={
                  formValues.checkInDate
                    ? formValues.checkInDate.substring(0, 10)
                    : ""
                }
                onChange={(_, { value }) => {
                  setFormValues((prev) => ({ ...prev, checkInDate: value }));
                  clearError('checkInDate');
                }}
              />
              {formErrors.checkInDate && (
                <Label basic color="red" pointing>
                  {formErrors.checkInDate}
                </Label>
              )}
            </Form.Field>

            <Form.Field required error={!!formErrors.checkOutDate}>
              <label>Check-out Date</label>
              <Input
                type="date"
                value={
                  formValues.checkOutDate
                    ? formValues.checkOutDate.substring(0, 10)
                    : ""
                }
                onChange={(_, { value }) => {
                  setFormValues((prev) => ({ ...prev, checkOutDate: value }));
                  clearError('checkOutDate');
                }}
              />
              {formErrors.checkOutDate && (
                <Label basic color="red" pointing>
                  {formErrors.checkOutDate}
                </Label>
              )}
            </Form.Field>

            <div style={{ marginTop: "1.5rem" }}>
              <Button
                secondary
                icon="check"
                content="Save"
                onClick={handleSave}
                loading={isSaving}
                disabled={isSaving}
              />
              <Button
                basic
                content="Cancel"
                onClick={closePanel}
                disabled={isSaving}
              />
            </div>
          </Form>
        </Segment>
      </SideOverlay>

      {/* ── Delete Confirmation ───────────────────────────────────────────── */}

      <Confirm
        open={!!deleteId}
        header="Delete Host Family Contact"
        content="Are you sure you want to delete this host family contact record? This cannot be undone."
        confirmButton={
          <Button
            negative
            content="Delete"
            loading={deleteMutation.isLoading}
          />
        }
        onCancel={() => setDeleteId(null)}
        onConfirm={() => deleteId && deleteMutation.mutate(deleteId)}
      />
    </div>
  );
};

export default HostFamilyContacts;
